import { Box, Button } from '@mui/material';
import { useEffect, useState, useMemo, type Dispatch, type SetStateAction } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from '@/i18n/use-translations';
import { useEventStore } from '@/state/stores/use-event-store';
import { useNotifications } from '@/hooks/use-notifications';
import { useAuth } from '@clerk/clerk-react';
import usePatchContributorMutation from '@/state/mutations/contributors/use-patch-contributor.ts';
import useCreateContributorMutation from '@/state/mutations/contributors/use-create-contributor.ts';
import {
  EVENT_CREW_ROLES,
  type ContributorDto,
  type CrewRole,
} from '@/types/dtos/contributors/response/contributor.dto';
import {
  contributorAlreadyHasRoleAssignment,
  contributorRolesMatchAssignment,
  getAcceptedEventIdsForRoleMatch,
  matchesEventCrewEntity,
} from '@/utils/contributor-extraction.utils';
import { getContributorProfileReviewIconColor } from '@/components/events/detail/tabs/sessions/session-actors/speaker-profile-review-status';
import { isContributorLinkedToClerkUser } from '@/utils/contributor-clerk-match.utils';
import { ContributorRoles } from '@/components/events/detail/tabs/contributors/contributor-roles.tsx';
import GeneralForm from './steps/general.form';
import MediaFilesForm from './steps/media-files.form';
import EventRolesForm from './steps/event-roles.form.tsx';
import StandardModal from '@/components/common/standard.modal';
import { getObjectId } from '@/helpers/object.id.helper';
import { useDeleteEventContributorRoles } from '@/state/mutations/contributors/use-delete-event-contributor-roles.ts';
import { useDeleteSessionContributor } from '@/state/mutations/sessions/use-delete-session-contributor.ts';
import { useDeleteFlowContributor } from '@/state/mutations/flows/use-delete-flow-contributor.ts';
import useCreateContributorRoleMutation from '@/state/mutations/contributors/use-create-contributor-role.ts';
import { useMarkProfileReviewedMutation } from '@/state/mutations/contributors/use-mark-profile-reviewed';
import { useBreakpointUp } from '@/hooks/use-breakpoint';

import { addUpdateContributorSchema, type StepsState, type RolesSchema } from './add-edit-contributor.schema';
import { menuButtons } from './add-edit-contributor.constants';
import type { MenuKey } from './add-edit-contributor.constants';

export default function AddEditContributorModal({
  contributor,
  open,
  setOpen,
  defaultTab = 'general',
  eventSlotId,
  profileOnly = false,
}: {
  contributor?: ContributorDto;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  defaultTab?: MenuKey;
  eventSlotId?: string;
  /** Flow schedule actor row: only Profile (general) fields, no side menu. */
  profileOnly?: boolean;
}) {
  const t = useTranslations();
  const notification = useNotifications();
  const { userId } = useAuth();
  const event = useEventStore((s) => s.event);
  const eventId = event?.id;
  const isWideScreen = useBreakpointUp('md');

  const profileOnlyTitle = useMemo(() => {
    if (!contributor) return t('event.detail.contributors.unknownContributor');
    return (
      contributor.profiles?.[0]?.displayName?.trim() ||
      contributor.email?.trim() ||
      t('event.detail.contributors.unknownContributor')
    );
  }, [contributor, t]);

  const [menuSelected, setMenuSelected] = useState<MenuKey>(defaultTab);
  const [initialValues, setInitialValues] = useState<StepsState | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [wasMarkedReviewed, setWasMarkedReviewed] = useState(false);
  const { mutateAsync: markProfileReviewedMutation } = useMarkProfileReviewedMutation();

  const { mutateAsync: patchContributorMutation, isPending: isPatchPending } = usePatchContributorMutation();
  const { mutateAsync: createContributorMutation } = useCreateContributorMutation();
  const { mutateAsync: deleteContributorRolesMutation, isPending: isDeleteContributorRolesPending } =
    useDeleteEventContributorRoles();
  const { mutateAsync: deleteSessionContributorMutation } = useDeleteSessionContributor();
  const { mutateAsync: deleteFlowContributorMutation } = useDeleteFlowContributor();
  const { mutateAsync: createContributorRoleMutation, isPending: isCreateContributorRolePending } =
    useCreateContributorRoleMutation();

  const { control, handleSubmit, reset, setValue } = useForm<StepsState>({
    resolver: zodResolver(addUpdateContributorSchema),
    defaultValues: {
      general: {
        fullName: '',
        title: '',
        organization: '',
        email: '',
        phoneNumber: '',
        biography: '',
        assetName: '',
      },
    },
  });

  const assignmentRoles = useWatch({ control, name: 'assignments.roles' });
  const emptyFormValues = useMemo(() => ({}) as StepsState, []);
  const currentValues = useWatch({ control }) ?? emptyFormValues;

  useEffect(() => {
    if (!open) {
      queueMicrotask(() => {
        setMenuSelected(profileOnly ? 'general' : defaultTab);
        setHasInteracted(false);
        setWasMarkedReviewed(false);
      });
    }
  }, [open, defaultTab, profileOnly]);

  useEffect(() => {
    if (open) {
      const initialFormValues: StepsState = {
        general: {
          fullName: contributor?.profiles?.[0]?.displayName || '',
          title: contributor?.profiles?.[0]?.title || '',
          organization: contributor?.profiles?.[0]?.organization || '',
          email: contributor?.email || '',
          phoneNumber: contributor?.phoneNumber || '',
          biography: contributor?.profiles?.[0]?.bio || '',
          assetName: contributor?.profiles?.[0]?.assetName || '',
        },
        assignments: {
          roles:
            contributor?.roles?.reduce((acc, r) => {
              if (r.entity) {
                acc.push({
                  role: r.role,
                  entity: getObjectId(r.entity),
                  entityType: r.entityType,
                });
              }
              return acc;
            }, [] as RolesSchema[]) || [],
        },
      };
      queueMicrotask(() => {
        setInitialValues(initialFormValues);
        reset(initialFormValues);
      });
    }
  }, [contributor, open, reset]);

  const onSubmit = async (values: StepsState) => {
    if (profileOnly) {
      if (!contributor || !contributor.id) {
        notification.notifyError(t('event.detail.addEditContributor.notifications.addEditContributorFailed'));
        return;
      }
    } else if (!eventId || !userId) {
      notification.notifyError(t('event.detail.addEditContributor.notifications.addEditContributorFailed'));
      return;
    }

    const currentProfile = contributor?.profiles?.[0];

    // Build profile data - create clean object with only fields we need
    // Map 'final' from DTO to 'isFinal' for backend schema
    const profileData: {
      displayName: string;
      title?: string;
      organization?: string;
      bio?: string;
      assetName?: string;
      id?: string;
      version?: number;
      editor?: string;
      isFinal?: boolean;
    } = {
      displayName: values.general.fullName,
      ...(values.general.title?.trim() && { title: values.general.title.trim() }),
      ...(values.general.organization?.trim() && { organization: values.general.organization.trim() }),
      ...(values.general.biography?.trim() && { bio: values.general.biography.trim() }),
      ...(values.general.assetName?.trim() && { assetName: values.general.assetName.trim() }),
    };

    // Preserve existing profile metadata when updating
    if (currentProfile) {
      profileData.id = currentProfile.id;
      profileData.version = currentProfile.version;
      profileData.editor = currentProfile.editor;
      // Map final (from DTO) to isFinal (for backend schema)
      profileData.isFinal = currentProfile.final;
    } else {
      profileData.editor = 'Event';
    }

    if (profileOnly) {
      try {
        if (!contributor?.id) {
          notification.notifyError(t('event.detail.addEditContributor.notifications.addEditContributorFailed'));
          return;
        }
        await patchContributorMutation({
          id: contributor.id,
          profiles: [profileData],
          phoneNumber: values.general.phoneNumber?.trim() === '' ? null : values.general.phoneNumber,
          email: values.general.email?.trim() === '' ? null : values.general.email,
        });
        notification.notifySuccess(t('event.detail.addEditContributor.notifications.addEditContributorSuccess'));
        const updatedFormValues: StepsState = {
          general: {
            fullName: values.general.fullName,
            title: values.general.title || '',
            organization: values.general.organization || '',
            email: values.general.email || '',
            phoneNumber: values.general.phoneNumber || '',
            biography: values.general.biography || '',
            assetName: values.general.assetName || '',
          },
          assignments: { roles: initialValues?.assignments?.roles ?? values.assignments?.roles ?? [] },
        };
        setInitialValues(updatedFormValues);
        reset(updatedFormValues);
        const profileWasReviewed = wasMarkedReviewed || getContributorProfileReviewIconColor(contributor) === 'success';
        if (profileWasReviewed && contributor.id) {
          await markProfileReviewedMutation(contributor.id).catch(() => {});
        }
        setHasInteracted(true);
      } catch (error) {
        console.error('Failed to save contributor:', error);
        notification.notifyError(t('event.detail.addEditContributor.notifications.addEditContributorFailed'));
      }
      return;
    }

    if (!eventId) {
      notification.notifyError(t('event.detail.addEditContributor.notifications.addEditContributorFailed'));
      return;
    }

    const assignmentRoles: RolesSchema[] = [...(values.assignments.roles || [])];
    if (contributor && userId && isContributorLinkedToClerkUser(contributor, userId) && event) {
      const acceptedIds = getAcceptedEventIdsForRoleMatch(event);
      const crewSet = new Set<string>([...EVENT_CREW_ROLES]);
      const formHasEventCrew = assignmentRoles.some((x) => x.entityType === 'Event' && crewSet.has(x.role as string));
      for (const r of contributor.roles) {
        if ((r as { deleted?: boolean }).deleted) continue;
        if (r.entityType !== 'Event' || !crewSet.has(r.role as string)) continue;
        if (!r.entity || !matchesEventCrewEntity(r.entity, acceptedIds)) continue;
        // User picked a different event crew role in the form; do not merge the old one back in.
        if (formHasEventCrew) continue;
        const entity = getObjectId(r.entity);
        const exists = assignmentRoles.some((x) => x.entity === entity && x.role === r.role);
        if (!exists) {
          assignmentRoles.push({ role: r.role as CrewRole, entity, entityType: 'Event' });
        }
      }
    }

    const payload = {
      event: eventId,
      profiles: [profileData],
      roles: assignmentRoles,
      phoneNumber: values.general.phoneNumber?.trim() === '' ? null : values.general.phoneNumber,
      email: values.general.email?.trim() === '' ? null : values.general.email,
    };

    try {
      if (!contributor?.id) {
        await createContributorMutation(payload);
      } else {
        const acceptedIds = getAcceptedEventIdsForRoleMatch(event);
        const crewSet = new Set<string>([...EVENT_CREW_ROLES]);

        const removedRoles = contributor.roles.filter((r) => {
          if ((r as { deleted?: boolean }).deleted || !r.entity) return false;
          const roleEntity = r.entity;
          if (payload.roles.some((p) => contributorRolesMatchAssignment(r, p, acceptedIds))) {
            return false;
          }
          if (r.entityType === 'Event' && crewSet.has(r.role as string)) {
            return true;
          }
          return !payload.roles.some((p) => getObjectId(roleEntity) === p.entity && r.entityType === p.entityType);
        });

        if (removedRoles.length > 0) {
          const removedEventRoleIds = removedRoles.filter((r) => r.entityType === 'Event').map((r) => r.id);
          if (removedEventRoleIds.length > 0) {
            await deleteContributorRolesMutation({
              contributorId: contributor.id,
              roleIds: removedEventRoleIds,
            });
          }

          await Promise.allSettled(
            removedRoles.reduce<Promise<unknown>[]>((acc, r) => {
              if (!r.entity || !contributor.id) return acc;
              const entityId = getObjectId(r.entity);
              if (!entityId) return acc;
              if (r.entityType === 'Session') {
                acc.push(
                  deleteSessionContributorMutation({
                    sessionId: entityId,
                    contributorId: contributor.id,
                  }),
                );
              } else if (r.entityType === 'Flow') {
                acc.push(
                  deleteFlowContributorMutation({
                    flowId: entityId,
                    contributorId: contributor.id,
                  }),
                );
              }
              return acc;
            }, []),
          );
        }

        const newRoles = payload.roles.filter(
          (role) => !contributorAlreadyHasRoleAssignment(contributor, role, acceptedIds),
        );
        if (newRoles.length > 0) {
          await Promise.all(
            newRoles.map((role) =>
              createContributorRoleMutation({
                contributorId: contributor.id,
                role,
                eventSlotId,
              }),
            ),
          );
        }

        // Don't pass roles to patch mutation since they're handled separately
        // Note: event field is not part of the patch schema, so we don't send it
        const patchPayload = {
          id: contributor.id,
          profiles: [profileData],
          phoneNumber: payload.phoneNumber,
          email: payload.email,
        };
        console.log('Calling patchContributorMutation with:', JSON.stringify(patchPayload, null, 2));
        const result = await patchContributorMutation(patchPayload);
        console.log('patchContributorMutation result:', result);
      }

      notification.notifySuccess(t('event.detail.addEditContributor.notifications.addEditContributorSuccess'));
      const updatedFormValues: StepsState = {
        general: {
          fullName: values.general.fullName,
          title: values.general.title || '',
          organization: values.general.organization || '',
          email: values.general.email || '',
          phoneNumber: values.general.phoneNumber || '',
          biography: values.general.biography || '',
          assetName: values.general.assetName || '',
        },
        assignments: {
          roles: assignmentRoles,
        },
      };
      setInitialValues(updatedFormValues);
      reset(updatedFormValues);
      const profileWasReviewed =
        wasMarkedReviewed || (contributor && getContributorProfileReviewIconColor(contributor) === 'success');
      if (profileWasReviewed && contributor?.id) {
        await markProfileReviewedMutation(contributor.id).catch(() => {});
      }
      setHasInteracted(true);
    } catch (error) {
      console.error('Failed to save contributor:', error);
      notification.notifyError(t('event.detail.addEditContributor.notifications.addEditContributorFailed'));
    }
  };

  // Check if there are any changes compared to initial values
  const hasChanges = useMemo(() => {
    if (!initialValues) {
      // For new contributors, check if form has any values
      return !!(currentValues.general?.fullName && currentValues.general.fullName.length >= 2);
    }

    const current = currentValues;
    const initial = initialValues;

    // Normalize values for comparison (treat undefined/null as empty string)
    const normalize = (val: string | undefined | null) => (val || '').trim();

    // Check general fields
    const generalChanged =
      normalize(current.general?.fullName) !== normalize(initial.general?.fullName) ||
      normalize(current.general?.title) !== normalize(initial.general?.title) ||
      normalize(current.general?.organization) !== normalize(initial.general?.organization) ||
      normalize(current.general?.email) !== normalize(initial.general?.email) ||
      normalize(current.general?.phoneNumber) !== normalize(initial.general?.phoneNumber) ||
      normalize(current.general?.biography) !== normalize(initial.general?.biography) ||
      normalize(current.general?.assetName) !== normalize(initial.general?.assetName);

    if (profileOnly) {
      return generalChanged;
    }

    // Check roles changes
    const currentRoles = current.assignments?.roles || [];
    const initialRoles = initial.assignments?.roles || [];
    const rolesChanged =
      currentRoles.length !== initialRoles.length ||
      currentRoles.some(
        (role) =>
          !initialRoles.some(
            (r) => r.role === role.role && r.entity === role.entity && r.entityType === role.entityType,
          ),
      ) ||
      initialRoles.some(
        (role) =>
          !currentRoles.some(
            (r) => r.role === role.role && r.entity === role.entity && r.entityType === role.entityType,
          ),
      );

    return generalChanged || rolesChanged;
  }, [currentValues, initialValues, profileOnly]);

  const isLoading = isPatchPending || isDeleteContributorRolesPending || isCreateContributorRolePending;
  const isSaveDisabled = !hasChanges || isLoading;

  const handleSaveClick = () => {
    // Trigger form submission programmatically using handleSubmit
    handleSubmit(onSubmit)();
  };

  const footer = (
    <>
      <Button variant="outlined" color="info" size="small" onClick={() => setOpen(false)}>
        {hasInteracted ? t('common.close') : t('common.cancel')}
      </Button>
      <Button
        type="button"
        data-testid="add-edit-contributor-button"
        variant="contained"
        color="info"
        size="small"
        disabled={isSaveDisabled}
        loading={isLoading}
        onClick={handleSaveClick}
      >
        {t('common.save')}
      </Button>
    </>
  );

  return (
    <StandardModal<MenuKey>
      open={open}
      close={() => setOpen(false)}
      menu={!profileOnly}
      menuButtons={profileOnly ? [] : menuButtons}
      menuSelected={menuSelected}
      onMenuSelect={(key) => setMenuSelected(key)}
      title={
        profileOnly
          ? profileOnlyTitle
          : contributor
            ? t('event.detail.addEditContributor.editTitle')
            : t('event.detail.addEditContributor.addTitle')
      }
      subtitle={profileOnly ? t('event.detail.contributors.manageProfile') : undefined}
      footer={footer}
      size="lg"
      height={isWideScreen ? '800px' : '600px'}
      maxHeight={isWideScreen ? 'calc(100vh - 64px)' : 'calc(100vh - 32px)'}
      width={isWideScreen ? '1000px' : undefined}
      collapsibleMenu={!profileOnly}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Box sx={{ flex: 1 }}>
          {profileOnly ? (
            <GeneralForm
              control={control}
              contributor={contributor}
              setValue={setValue}
              onMarkReviewed={() => {
                setHasInteracted(true);
                setWasMarkedReviewed(true);
              }}
            />
          ) : (
            <>
              {menuSelected === 'general' && (
                <GeneralForm
                  control={control}
                  contributor={contributor}
                  setValue={setValue}
                  onMarkReviewed={() => {
                    setHasInteracted(true);
                    setWasMarkedReviewed(true);
                  }}
                />
              )}
              {menuSelected === 'assignments' && contributor && (
                <Box sx={{ mt: '1rem' }}>
                  <ContributorRoles contributor={contributor} />
                </Box>
              )}
              {menuSelected === 'mediaFiles' && <MediaFilesForm />}
              {menuSelected === 'crewRoles' && eventId && assignmentRoles && (
                <EventRolesForm
                  eventId={eventId}
                  roles={assignmentRoles}
                  setValue={setValue}
                  contributor={contributor}
                />
              )}
            </>
          )}
        </Box>
      </Box>
    </StandardModal>
  );
}
